<template>
  <div class="container py-5">

    <button
      :disabled="!valid" 
      class="button is-primary mb-5"
      @click="save()">
      Save Delivery
    </button>

    <v-form 
      :valid.sync="valid" 
      class="columns is-multiline">

      <div class="column is-one-quarter">
        <label class="label">Project*</label>
        <search-autocomplete
          v-model="delivery.project_id"
          :project.sync="project"
          project-stage="Closed Won">
        </search-autocomplete>
      </div>

      <div class="column is-half">
        <label class="label">Description</label>
        <v-text-field 
          placeholder="Enter delivery description"
          v-model="delivery.delivery_desc">
        </v-text-field>
      </div>

      <div class="column is-one-quarter">
        <label class="label">Expected Delivery Date*</label>
        <v-datepicker 
          required
          placeholder="Expected delivery date"
          v-model="delivery.exp_delivery_date">
        </v-datepicker>
      </div>
      
      <div class="column is-2">
        <label class="label">Delivery Contact Email*</label>
        <v-text-field
          required 
          placeholder="Enter delivery contact email"
          v-model="delivery.email">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Delivery Contact Phone*</label>
        <v-text-field
          required 
          placeholder="Enter delivery contact phone"
          v-model="delivery.phone1">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Site Manager Name*</label>
        <v-text-field
          required 
          placeholder="Enter site manager name"
          v-model="delivery.site_manager_name">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Site Manager Email*</label>
        <v-text-field
          required 
          placeholder="Enter site manager email"
          v-model="delivery.site_manager_email">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Project Manager Name*</label>
        <v-text-field
          required 
          placeholder="Enter project manager name"
          v-model="delivery.project_manager_name">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Site Manager Email*</label>
        <v-text-field
          required 
          placeholder="Enter project manager email"
          v-model="delivery.project_manager_email">
        </v-text-field>
      </div>

      <div class="column is-6">
        <label class="label">Delivery Address*</label>
        <v-text-field 
          required
          placeholder="Enter delivery address"
          v-model="delivery.address">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Delivery City*</label>
        <v-text-field 
          required
          placeholder="Enter delivery city"
          v-model="delivery.city">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Delivery Province/ State*</label>
        <v-text-field 
          required
          placeholder="Enter delivery province/ state"
          v-model="delivery.province_state">
        </v-text-field>
      </div>

      <div class="column is-2">
        <label class="label">Delivery Postal Code*</label>
        <v-text-field 
          required
          placeholder="Enter delivery postal code"
          v-model="delivery.postal_code">
        </v-text-field>
      </div>

    </v-form>

    <hr/>

    <div class="columns">

      <div class="column is-half">

        <div class="subtitle">Warehouse Items</div>

        <article 
          v-if="!loading && projectInventory.length == 0" 
          class="message is-info is-fullwidth">
          <div class="message-body">
            Choose a project to see product needs and inventory available.
          </div>
        </article>

        <table 
          class="table is-fullwidth has-small-font sticky-header" 
          v-if="!loading && projectInventory.length > 0">

          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in headers"
                :key="headerIndex">
                {{ header }}
              </th>
            </tr>
          </thead>

          <tbody>
            <template 
              v-for="inventoryItem in projectInventory">
              <tr
                class="v-centered subheader"
                :key="inventoryItem.id">

                <td>
                  {{ inventoryItem.inventory.product_master.product_category.category }}
                </td>         
                <td>
                  <span v-if="inventoryItem.inventory.product_master.product_subcategory">
                    {{ inventoryItem.inventory.product_master.product_subcategory.subcategory }}
                  </span>
                  <span v-else class="has-text-grey">
                    No subcategory
                  </span>
                </td>  
                <td>
                  {{ inventoryItem.inventory.product_master.name }}
                </td>
                <td class="text--nowrap is-size-7">
                  <template v-if="inventoryItem.inventory.product_master.product_category.has_dimensions">
                    {{ lwt(inventoryItem.inventory.stocking_unit) }}
                  </template>
                  <span>
                    {{ inventoryItem.inventory.product_master.size }}
                  </span>
                </td>
                <td>
                  {{ inventoryItem.qty_needed }}
                </td>
                <td>
                  {{ inventoryItem.qty_available }}
                </td>

              </tr>

              <template
                v-if="inventoryItem.inventory.stocking_unit && 
                  inventoryItem.inventory.stocking_unit.storage_unit.length > 0">
                <tr 
                  :key="inventoryItem.id + '-warehouse-locations-header'"
                  class="v-centered is-narrow has-background-light has-tiny-font">
                  <th></th>
                  <th>Warehouse</th>
                  <th>Location</th>
                  <th>Storage</th>
                  <th>Storage Type</th>
                  <th>Quantity Stored</th>
                </tr>
                <tr 
                  class="has-background-light v-centered"
                  v-for="storageUnit in inventoryItem.inventory.stocking_unit.storage_unit"
                  :key="storageUnit.id">
                  <td>
                    <v-checkbox
                      :value="deliveryStorageIds.includes(storageUnit.storage_id)"
                      @input="toggleStorageId(storageUnit.storage_id)">
                    </v-checkbox>
                  </td>
                  <td>
                    <span v-if="storageUnit.storage.warehouse_location">
                      ({{ storageUnit.storage.warehouse_location.warehouse.warehouse_code }}) 
                      {{ storageUnit.storage.warehouse_location.warehouse.warehouse_name }}
                    </span>
                    <span v-else class="has-text-grey-light">
                      No warehouse information
                    </span>
                  </td>
                  <td>
                    <span v-if="storageUnit.storage.warehouse_location">
                      ({{ storageUnit.storage.warehouse_location.location_code }}) 
                      {{ storageUnit.storage.warehouse_location.location_name }}
                    </span>
                    <span v-else class="has-text-grey-light">
                      No warehouse space information
                    </span>
                  </td>
                  <td>
                    ({{ storageUnit.storage.storage_code }})
                    {{ storageUnit.storage.storage_name }}
                  </td>
                  <td>
                    {{ storageUnit.storage.storage_type.storage_type_name }}
                  </td>
                  <td>
                    {{ storageUnit.qty }}
                  </td>
                </tr>
              </template>

            </template>
          </tbody>
        </table>

      </div>

      <div class="column is-half">
        <div class="subtitle">Delivery Items</div>
        <table 
          class="table is-fullwidth has-small-font sticky-header"
          v-if="!loading && deliveryStorages.length > 0">
          <thead>
            <tr>
              <th 
                v-for="(header, headerIndex) in deliveryHeaders"
                :key="headerIndex">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="inventoryItem in deliveryStorages">
              <tr               
                v-for="storageUnit in inventoryItem.inventory.stocking_unit.storage_unit"
                :key="inventoryItem.id + '-' + storageUnit.id">
                <td>
                  {{ inventoryItem.inventory.product_master.product_category.category }}
                </td>         
                <td>
                  <span v-if="inventoryItem.inventory.product_master.product_subcategory">
                    {{ inventoryItem.inventory.product_master.product_subcategory.subcategory }}
                  </span>
                  <span v-else class="has-text-grey">
                    No subcategory
                  </span>
                </td>  
                <td>
                  <div>
                    {{ inventoryItem.inventory.product_master.name }}
                  </div>
                  <div class="help">
                    <span v-if="inventoryItem.inventory.product_master.product_category.has_dimensions">
                      {{ lwt(inventoryItem.inventory.stocking_unit) }}
                    </span>
                    <span>
                      {{ inventoryItem.inventory.product_master.size }}
                    </span>
                  </div>
                </td>
                <td class="is-size-7">
                  <div>
                    ({{ storageUnit.storage.warehouse_location.warehouse.warehouse_code }}) 
                    {{ storageUnit.storage.warehouse_location.warehouse.warehouse_name }}
                  </div>
                  <div>
                    ({{ storageUnit.storage.warehouse_location.location_code }}) 
                    {{ storageUnit.storage.warehouse_location.location_name }}
                  </div>
                </td>
                <td>
                  {{ storageUnit.qty }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VDatepicker from '@/components/base/VDatepicker'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VForm, VTextField, SearchAutocomplete, VDatepicker, VCheckbox 
  },
  data() {
    return {
      project: {},
      delivery: {
        project_id: null,
        delivery_desc: null,
        address: null,
        city: null,
        province_state: null,
        postal_code: null,
        exp_delivery_date: null,
        email: null,
        phone1: null,
        site_manager_name: null,
        site_manager_email: null,
        project_manager_name: null,
        project_manager_email: null
      },
      valid: false,
      headers: [
        'Product Category', 'Product Subcategory', 'Product', 
        'Product Size', 'Quantity Needed', 'Quantity Available'
      ],
      deliveryHeaders: [
        'Product Category', 'Product Subcategory', 'Product', 'Location',
        'Delivery Quantity'
      ],
      deliveryStorageIds: []
    }
  },
  computed: {
    ...mapState("inventoryProject", ["projectInventory", "loading"]),
    deliveryStorages() {
      return this.projectInventory.map(d => {
        let res = JSON.parse(JSON.stringify(d));
        res.inventory.stocking_unit.storage_unit = res.inventory.stocking_unit ? 
          res.inventory.stocking_unit.storage_unit.filter(
            d => this.deliveryStorageIds.includes(d.storage_id)
          ) : []
        return res
      }).filter(d => d.inventory.stocking_unit.storage_unit.length > 0);
    }
  },
  watch: {
    'delivery.project_id'(id) {
      this.getInventory({ project_id: id });
      this.autoPopulateProjectAddress();
    }
  },
  methods: {
    ...mapActions("inventoryProject", ["getInventory"]),
    ...mapActions("deliveries", ["addDelivery", "addItem"]),
    ...mapMutations("app", ["setNotification"]),
    lwt(product) {
      if (product) {
        return `L${product.length_in_str}" x W${product.width_in_str}" x T${product.thick_in_str}"`
      }
    },
    autoPopulateProjectAddress() {
      const { project } = this;
      this.delivery.address = project.address;
      this.delivery.city = project.crm_object.City_District;
      this.delivery.province_state = project.crm_object.State_Province;
      this.delivery.postal_code = project.crm_object.Postal_Code;
    },
    toggleStorageId(id) {
      let index = this.deliveryStorageIds.indexOf(id);
      if (index == -1) {
        this.deliveryStorageIds.push(id);
      } else {
        this.deliveryStorageIds.splice(index, 1);
      }
    },
    save() {
      this.addDelivery(this.delivery).then(resp => {

        let items = this.deliveryStorages.map(d => 
          d.inventory.stocking_unit.storage_unit
        ).flat(1).map(d => {
          return {
            delivery_id: resp.data.id,
            storage_id: d.storage_id
          }
        });

        let promises = [];

        items.forEach(item => {
          promises.push(this.addItem(item));
        });

        Promise.all(promises).then(() => {
          this.setNotification({
            status: true,
            type: 'success',
            message: 'Delivery successfully created'
          });

          this.$router.push({
            name: 'DeliveryList'
          });
        });

      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>